import type { Plan } from './plans';

/** The coupon fields the plan-coupon picker needs from the coupons API. */
export interface CouponOption {
  promo_code: string;
  status: 'inactive' | 'active' | 'unknown' | 'deleted' | 'internal';
  internal_coupon_name?: string;
  duration?: number;
  // The discount: dollars off each month, or a percentage.
  dollar_amount?: number;
  percentage?: number;
  plan_ids: ReadonlyArray<string>;
  addon_ids: ReadonlyArray<string>;
}

// A plan the API sent without a coupon at all reads as no coupon: see
// planFromApi. Belt and braces, because this runs on every keystroke in the
// plan form and a throw here is a white screen.
export const planCouponErrors = (plan: Plan): string[] => (
  plan.apply_embedded_coupon && (plan.embedded_coupon ?? '').trim() === ''
    ? ['Plan coupon is switched on but no coupon is selected']
    : []
);

export const planPromoCodeErrors = (plan: Plan): string[] => (
  plan.apply_promo_code && (plan.promo_code ?? '').trim() === ''
    ? ['Promo code is switched on but no code is selected']
    : []
);

/**
 * The coupons an operator may pick: everything not deleted, sorted by
 * code, with the plan's current coupon first even when it is deleted or
 * unknown to the table, so the form never hides the value it holds.
 */
export const selectableCoupons = (coupons: ReadonlyArray<CouponOption>, current: string): CouponOption[] => {
  const live = coupons
    .filter((c) => c.status !== 'deleted' && c.promo_code !== current)
    .sort((a, b) => a.promo_code.localeCompare(b.promo_code));
  if (!current) return live;
  const held = coupons.find((c) => c.promo_code === current)
    ?? {
      promo_code: current, status: 'unknown' as const, plan_ids: [], addon_ids: [],
    };
  return [held, ...live];
};

export const couponOptionLabel = (c: CouponOption): string => [
  c.promo_code,
  c.internal_coupon_name,
  c.duration ? `${c.duration} month${c.duration === 1 ? '' : 's'}` : undefined,
].filter(Boolean).join(' · ');

export const planCouponSummary = (plan: Plan): string => {
  if (!plan.embedded_coupon) return 'none';
  return plan.apply_embedded_coupon ? plan.embedded_coupon : `${plan.embedded_coupon} (off)`;
};
